import { FC, ReactNode } from 'react';
import Link from 'next/link';

type CtaButtonVariant = 'primary' | 'callout' | 'secondary';

type CtaButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  /** When set, renders a link (e.g. the paywall checkout CTA) instead of a button. */
  href?: string;
  variant?: CtaButtonVariant;
  type?: 'button' | 'submit';
  disabled?: boolean;
  dataCy?: string;
};

const VARIANT_CLASSES: Record<CtaButtonVariant, string> = {
  // Conversion cyan, used for the forward CTA on each step.
  primary: 'bg-primary-500 text-white hover:bg-primary-600',
  // Purple secondary callout (the "Import my team" screenshot launcher).
  callout: 'bg-[#2D0A3A] text-white hover:bg-[#3C0F4E]',
  // White outlined alternative (e.g. "Build manually").
  secondary:
    'bg-white text-neutral-800 border border-neutral-300 hover:border-neutral-400'
};

// 8px radius (rounded-lg) to match the website; shared by every funnel CTA.
const BASE_CLASSES =
  'block w-full rounded-lg px-6 py-4 text-center font-body text-base font-bold transition-colors';

/**
 * The single shared step-funnel CTA (GYTR + readiness + paywall). Three
 * variants, no arrow icons, 8px radius. Renders a link when `href` is set
 * (e.g. the paywall checkout), otherwise a button — so every funnel CTA
 * shares one look and there's no radius/size drift.
 */
const CtaButton: FC<CtaButtonProps> = ({
  children,
  onClick,
  href,
  variant = 'primary',
  type = 'button',
  disabled = false,
  dataCy
}) => {
  const className = `${BASE_CLASSES} ${
    disabled
      ? 'cursor-not-allowed bg-neutral-200 text-neutral-400'
      : VARIANT_CLASSES[variant]
  }`;

  if (href && !disabled) {
    return (
      <Link
        href={href}
        data-cy={dataCy}
        onClick={onClick}
        className={`${className} no-underline`}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-cy={dataCy}
      className={className}
    >
      {children}
    </button>
  );
};

export default CtaButton;
